import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { RecipesApi, SuppliesApi, UnitsApi } from '../api/resources';
import { ApiError } from '../api/client';
import type { ComponentCostLine, Recipe, RecipeCostResult, Supply, Unit } from '../api/types';
import { useToast } from '../context/ToastContext';
import { formatCOP, formatPercent, formatQty } from '../utils/format';
import { ImageLightbox } from '../components/ImageLightbox';

function ComponentTree({ lines }: { lines: ComponentCostLine[] }) {
  return (
    <ul>
      {lines.map((line) => (
        <li key={line.componentId}>
          <strong>{line.type === 'RECETA' ? '🧩 ' : ''}{line.name}</strong> &mdash; {formatQty(line.quantity)} {line.unitName} x{' '}
          {formatCOP(line.unitCost)} = <strong>{formatCOP(line.subtotal)}</strong>
          {line.subComponents && line.subComponents.length > 0 && <ComponentTree lines={line.subComponents} />}
        </li>
      ))}
    </ul>
  );
}

export function RecipeDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { notify } = useToast();
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [cost, setCost] = useState<RecipeCostResult | null>(null);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [supplies, setSupplies] = useState<Supply[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);

  const [editForm, setEditForm] = useState({ name: '', description: '', laborCost: '', wastePercentage: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [savingImage, setSavingImage] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const [componentType, setComponentType] = useState<'INSUMO' | 'RECETA'>('INSUMO');
  const [componentRef, setComponentRef] = useState('');
  const [componentQty, setComponentQty] = useState('');
  const [componentUnit, setComponentUnit] = useState('');
  const [savingComponent, setSavingComponent] = useState(false);

  async function loadAll() {
    if (!id) return;
    setLoading(true);
    try {
      const [recipeRes, costRes, recipesRes, suppliesRes, unitsRes] = await Promise.all([
        RecipesApi.get(id),
        RecipesApi.cost(id, 1),
        RecipesApi.list({ isActive: true }),
        SuppliesApi.list({ isActive: true, pageSize: 200 }),
        UnitsApi.list(),
      ]);
      setRecipe(recipeRes.recipe);
      setCost(costRes.cost);
      setRecipes(recipesRes.recipes.filter((r) => r.id !== id));
      setSupplies(suppliesRes.items);
      setUnits(unitsRes.units);
      setEditForm({
        name: recipeRes.recipe.name,
        description: recipeRes.recipe.description ?? '',
        laborCost: recipeRes.recipe.laborCost,
        wastePercentage: recipeRes.recipe.wastePercentage,
      });
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible cargar la receta.', 'error');
      navigate('/recetas');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function handleEditSubmit(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setSavingEdit(true);
    try {
      await RecipesApi.update(id, {
        name: editForm.name,
        description: editForm.description || undefined,
        laborCost: Number(editForm.laborCost),
        wastePercentage: Number(editForm.wastePercentage),
      });
      notify('Receta actualizada.', 'success');
      loadAll();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible actualizar la receta.', 'error');
    } finally {
      setSavingEdit(false);
    }
  }

  async function handleAddComponent(e: FormEvent) {
    e.preventDefault();
    if (!id || !componentRef || !componentQty || !componentUnit) {
      notify('Completa insumo/receta, cantidad y unidad.', 'error');
      return;
    }
    setSavingComponent(true);
    try {
      await RecipesApi.addComponent(id, {
        supplyId: componentType === 'INSUMO' ? componentRef : undefined,
        componentRecipeId: componentType === 'RECETA' ? componentRef : undefined,
        quantity: Number(componentQty),
        unitId: componentUnit,
      });
      notify('Componente agregado.', 'success');
      setComponentRef('');
      setComponentQty('');
      setComponentUnit('');
      loadAll();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible agregar el componente.', 'error');
    } finally {
      setSavingComponent(false);
    }
  }

  async function handleImageUpload() {
    if (!id || !imageFile) return;
    setSavingImage(true);
    try {
      const result = await RecipesApi.uploadImage(id, imageFile);
      setRecipe((current) => (current ? { ...current, imageUrl: result.recipe.imageUrl } : current));
      setImageFile(null);
      notify('Imagen del arreglo actualizada.', 'success');
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible cargar la imagen.', 'error');
    } finally {
      setSavingImage(false);
    }
  }

  async function handleImageRemove() {
    if (!id || !recipe?.imageUrl || !window.confirm('¿Eliminar la imagen de este arreglo?')) return;
    setSavingImage(true);
    try {
      await RecipesApi.removeImage(id);
      setRecipe((current) => (current ? { ...current, imageUrl: null } : current));
      notify('Imagen eliminada.', 'success');
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible eliminar la imagen.', 'error');
    } finally {
      setSavingImage(false);
    }
  }

  async function handleRemoveComponent(componentId: string) {
    if (!id) return;
    if (!window.confirm('¿Eliminar este componente de la receta?')) return;
    try {
      await RecipesApi.removeComponent(id, componentId);
      notify('Componente eliminado.', 'success');
      loadAll();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible eliminar el componente.', 'error');
    }
  }

  if (loading) return <div className="loading-block">Cargando receta...</div>;
  if (!recipe || !cost) return null;

  return (
    <div>
      <div className="flex-between">
        <div>
          <h1>{recipe.name}</h1>
          <p className="text-muted">Codigo: {recipe.code}</p>
        </div>
        <span className={`pill ${recipe.isActive ? 'pill-active' : 'pill-inactive'}`}>{recipe.isActive ? 'Activo' : 'Inactivo'}</span>
      </div>

      <div className="card">
        <h2>Datos generales</h2>
        <form onSubmit={handleEditSubmit} noValidate>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="ename">Nombre</label>
              <input id="ename" required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="elabor">Mano de obra (COP)</label>
              <input
                id="elabor"
                type="number"
                min={0}
                step="0.01"
                required
                value={editForm.laborCost}
                onChange={(e) => setEditForm({ ...editForm, laborCost: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="ewaste">Merma (%)</label>
              <input
                id="ewaste"
                type="number"
                min={0}
                max={100}
                step="0.01"
                required
                value={editForm.wastePercentage}
                onChange={(e) => setEditForm({ ...editForm, wastePercentage: e.target.value })}
              />
            </div>
          </div>
          <div className="field">
            <label htmlFor="edesc">Descripcion</label>
            <textarea id="edesc" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
          </div>
          <button type="submit" className="btn btn-primary" disabled={savingEdit}>
            {savingEdit ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </form>
      </div>

      <div className="card recipe-image-card">
        <div className="card-header">
          <div>
            <h2>Imagen del arreglo</h2>
            <p className="text-muted">Opcional. Puedes cargarla o reemplazarla cuando lo necesites.</p>
          </div>
          {recipe.imageUrl && (
            <button type="button" className="recipe-image-button" onClick={() => setLightboxOpen(true)} aria-label={`Ampliar imagen de ${recipe.name}`}>
              <img className="recipe-image-preview" src={recipe.imageUrl} alt={`Imagen de ${recipe.name}`} />
            </button>
          )}
        </div>
        <div className="actions-row">
          <label className="btn btn-secondary" htmlFor="recipe-image-input">
            {recipe.imageUrl ? 'Cambiar imagen' : 'Seleccionar imagen'}
          </label>
          <input
            id="recipe-image-input"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            onChange={(e) => setImageFile(e.target.files?.[0] ?? null)}
          />
          {imageFile && <span className="text-muted">{imageFile.name}</span>}
          <button type="button" className="btn btn-primary" disabled={!imageFile || savingImage} onClick={handleImageUpload}>
            {savingImage ? 'Guardando...' : 'Guardar imagen'}
          </button>
          {recipe.imageUrl && (
            <button type="button" className="btn btn-danger" disabled={savingImage} onClick={handleImageRemove}>
              Eliminar imagen
            </button>
          )}
        </div>
        <p className="field hint">Formatos permitidos: JPG, PNG o WebP. Tamaño máximo: 5 MB.</p>
      </div>

      {lightboxOpen && recipe.imageUrl && <ImageLightbox imageUrl={recipe.imageUrl} title={recipe.name} onClose={() => setLightboxOpen(false)} />}

      <div className="card">
        <h2>Componentes</h2>
        {recipe.components && recipe.components.length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Nombre</th>
                  <th className="text-right">Cantidad</th>
                  <th>Unidad</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {recipe.components.map((c) => (
                  <tr key={c.id}>
                    <td>{c.componentRecipeId ? 'Receta' : 'Insumo'}</td>
                    <td>{c.supply?.name ?? c.componentRecipe?.name}</td>
                    <td className="text-right">{formatQty(c.quantity)}</td>
                    <td>{c.unit?.name}</td>
                    <td>
                      <button type="button" className="btn btn-danger btn-sm" onClick={() => handleRemoveComponent(c.id)}>
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="empty-state">Esta receta aun no tiene componentes.</p>
        )}

        <h3 className="mt-1">Agregar componente</h3>
        <form onSubmit={handleAddComponent} noValidate>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="ctype">Tipo</label>
              <select id="ctype" value={componentType} onChange={(e) => { setComponentType(e.target.value as never); setComponentRef(''); }}>
                <option value="INSUMO">Insumo</option>
                <option value="RECETA">Receta (subarreglo)</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="cref">{componentType === 'INSUMO' ? 'Insumo' : 'Receta'}</label>
              <select id="cref" required value={componentRef} onChange={(e) => setComponentRef(e.target.value)}>
                <option value="">Selecciona...</option>
                {(componentType === 'INSUMO' ? supplies : recipes).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.code} - {item.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="cqty">Cantidad</label>
              <input id="cqty" type="number" min={0.001} step="0.001" required value={componentQty} onChange={(e) => setComponentQty(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="cunit">Unidad</label>
              <select id="cunit" required value={componentUnit} onChange={(e) => setComponentUnit(e.target.value)}>
                <option value="">Selecciona...</option>
                {units.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={savingComponent}>
            {savingComponent ? 'Agregando...' : 'Agregar componente'}
          </button>
        </form>
      </div>

      <div className="card">
        <h2>Desglose de costos (por unidad)</h2>
        <div className="component-tree">
          <ComponentTree lines={cost.components} />
        </div>
        <table style={{ marginTop: '1rem', minWidth: 0 }}>
          <tbody>
            <tr>
              <td>Costo de materiales</td>
              <td className="text-right">{formatCOP(cost.materialsCost)}</td>
            </tr>
            <tr>
              <td>Merma ({formatPercent(cost.wastePercentage)})</td>
              <td className="text-right">{formatCOP(cost.wasteCost)}</td>
            </tr>
            <tr>
              <td>Mano de obra</td>
              <td className="text-right">{formatCOP(cost.laborCost)}</td>
            </tr>
            <tr style={{ fontWeight: 700, borderTop: '2px solid var(--color-border)' }}>
              <td>Costo de produccion (por unidad)</td>
              <td className="text-right">{formatCOP(cost.productionCost)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
